import { useState, useEffect } from "react";
import "./css/loader.css";

/**
 * Loader mejorado con animaciones suaves y múltiples variantes
 *
 * @param {string} type - Tipo de loader: "spinner" | "linear" | "dots" | "bar"
 * @param {string} text - Texto a mostrar debajo del loader
 * @param {string} size - Tamaño: "sm" | "md" | "lg"
 * @param {boolean} center - Centrar el loader
 * @param {boolean} overlay - Mostrar con overlay de pantalla completa
 * @param {number} delay - Delay en ms antes de mostrar (evita flash en cargas rápidas)
 * @param {string} className - Clases CSS adicionales
 *
 * @example
 * // Loader simple para listas
 * <Loader center text="Cargando datos..." delay={200} />
 *
 * @example
 * // Loader con puntos para modales
 * <Loader center type="dots" text="Guardando..." />
 *
 * @example
 * // Loader de pantalla completa
 * <Loader overlay text="Procesando..." size="lg" />
 */
const Loader = ({
  type = "spinner", // spinner | linear | dots | bar
  text = "Cargando...",
  size = "md", // sm | md | lg
  center = false,
  overlay = false,
  delay = 0, // delay en ms antes de mostrar (evita flash en cargas rápidas)
  className = "",
}) => {
  const [show, setShow] = useState(delay === 0);

  useEffect(() => {
    if (delay > 0) {
      const timer = setTimeout(() => setShow(true), delay);
      return () => clearTimeout(timer);
    }
  }, [delay]);

  if (!show) return null;

  const sizeMap = {
    sm: { spinner: "16px", text: "text-sm", dots: "5px" },
    md: { spinner: "32px", text: "", dots: "7px" },
    lg: { spinner: "48px", text: "text-lg", dots: "9px" },
  };

  const currentSize = sizeMap[size];

  const renderLoader = () => {
    switch (type) {
      case "dots":
        return (
          <div className="loader-dots" style={{ fontSize: currentSize.dots }}>
            <div
              className="loader-dot"
              style={{ backgroundColor: "#6c757d" }}
            />
            <div
              className="loader-dot"
              style={{ backgroundColor: "#6c757d" }}
            />
            <div
              className="loader-dot"
              style={{ backgroundColor: "#6c757d" }}
            />
          </div>
        );

      case "bar":
        return (
          <div style={{ width: "100%", maxWidth: "300px" }}>
            <div className="loader-bar">
              <div className="loader-bar-fill" />
            </div>
          </div>
        );

      case "linear":
        return (
          <div style={{ width: "100%", maxWidth: "300px" }}>
            <div className="progress" style={{ height: "4px" }}>
              <div
                className="progress-bar bg-primary progress-bar-striped progress-bar-animated"
                role="progressbar"
                style={{ width: "100%" }}
              />
            </div>
          </div>
        );

      default: // spinner
        return (
          <div
            className="loader-spinner"
            style={{
              width: currentSize.spinner,
              height: currentSize.spinner,
              border: "2px solid rgba(108, 117, 125, 0.15)",
              borderTopColor: "#6c757d",
              borderRadius: "50%",
            }}
            role="status"
            aria-label={text}
          />
        );
    }
  };

  const content = (
    <div
      className={`loader-container ${center ? "text-center" : ""} ${delay > 0 ? "delayed" : ""} ${className}`}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: center ? "center" : "flex-start",
        gap: "12px",
        ...(center && { width: "100%" }),
      }}
    >
      {renderLoader()}
      {text && (
        <div
          className={`text-muted ${currentSize.text}`}
          style={{
            fontWeight: 500,
            animation: "fadeIn 0.3s ease-in-out",
          }}
        >
          {text}
        </div>
      )}
    </div>
  );

  if (overlay) {
    return (
      <div className="loader-overlay" onClick={(e) => e.stopPropagation()}>
        {content}
      </div>
    );
  }

  return content;
};

export default Loader;
